/* ============================================
   SUHARA — Wishlist + Recently Viewed (My Space)
   Client-side only: no wishlist app is installed on this store, so this
   persists to localStorage (per-browser, not cross-device). Recently Viewed
   reuses the existing 'viewedProducts' key written by @theme/recently-viewed-products
   so it stays in sync with the predictive search "recently viewed" feature.
   ============================================ */

window.Suhara = window.Suhara || {};

(function () {
  var WISHLIST_KEY = 'suhara_wishlist';
  var VIEWED_KEY = 'viewedProducts';

  function getWishlist() {
    try {
      return JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
    } catch (e) {
      return [];
    }
  }

  function setWishlist(handles) {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(handles));
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = handles.length;
    });
  }

  function has(handle) {
    return getWishlist().indexOf(handle) !== -1;
  }

  function add(handle) {
    var list = getWishlist();
    if (list.indexOf(handle) === -1) {
      list.unshift(handle);
      setWishlist(list);
    }
  }

  function remove(handle) {
    var list = getWishlist().filter(function (h) {
      return h !== handle;
    });
    setWishlist(list);
  }

  function toggle(handle) {
    var nowActive = !has(handle);
    if (nowActive) {
      add(handle);
    } else {
      remove(handle);
    }
    return nowActive;
  }

  function getViewedIds() {
    try {
      return JSON.parse(localStorage.getItem(VIEWED_KEY) || '[]');
    } catch (e) {
      return [];
    }
  }

  // Hydrate every wishlist heart button on the page (product cards, grids, etc.)
  // so the filled/unfilled state matches what's actually saved.
  function hydrateButtons() {
    document.querySelectorAll('.product-card__wishlist[data-product-handle]').forEach(function (btn) {
      var handle = btn.getAttribute('data-product-handle');
      btn.classList.toggle('is-active', has(handle));
    });
  }

  // Called from the onclick already present on the product card heart button.
  function handleToggleClick(btn) {
    var handle = btn.getAttribute('data-product-handle');
    if (!handle) return;
    var nowActive = toggle(handle);
    btn.classList.toggle('is-active', nowActive);
    if (window.Suhara && Suhara.toast) {
      Suhara.toast(nowActive ? 'Added to wishlist' : 'Removed from wishlist');
    }
  }

  function fetchAllProducts() {
    return fetch('/products.json?limit=250')
      .then(function (r) {
        return r.ok ? r.json() : { products: [] };
      })
      .then(function (data) {
        return data.products || [];
      })
      .catch(function () {
        return [];
      });
  }

  function buildMiniCard(templateEl, product, options) {
    var node = templateEl.content.firstElementChild.cloneNode(true);
    var image = product.images && product.images[0];
    var variant = product.variants && product.variants[0];

    var mediaLink = node.querySelector('.acct-mini-card__media');
    var img = node.querySelector('img');
    var titleLink = node.querySelector('.acct-mini-card__title');
    var priceEl = node.querySelector('.acct-mini-card__price');
    var removeBtn = node.querySelector('.acct-mini-card__remove');

    var url = '/products/' + product.handle;
    mediaLink.setAttribute('href', url);
    titleLink.setAttribute('href', url);
    titleLink.textContent = product.title;

    if (image) {
      img.src = image.src;
      img.alt = product.title;
    }

    if (variant) {
      priceEl.textContent = formatMoney(variant.price);
    }

    removeBtn.addEventListener('click', function () {
      if (options.onRemove) options.onRemove(product);
      node.remove();
    });

    if (options.removable === false) {
      removeBtn.hidden = true;
    }

    return node;
  }

  function formatMoney(priceString) {
    var amount = parseFloat(priceString);
    if (isNaN(amount)) return priceString;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
        style: 'currency',
        currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'INR',
        minimumFractionDigits: 0,
      }).format(amount);
    } catch (e) {
      return '₹' + amount;
    }
  }

  function renderDashboard() {
    var wishlistGrid = document.querySelector('[data-wishlist-grid]');
    var wishlistEmpty = document.querySelector('[data-wishlist-empty]');
    var clearBtn = document.querySelector('[data-wishlist-clear]');
    var template = document.getElementById('acct-mini-card-template');

    if (!template) return;

    var wishlistHandles = getWishlist();

    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = wishlistHandles.length;
    });

    if (wishlistHandles.length === 0) {
      if (wishlistEmpty) wishlistEmpty.hidden = false;
      return renderViewed();
    }

    if (clearBtn) {
      clearBtn.hidden = false;
      clearBtn.addEventListener('click', function () {
        setWishlist([]);
        wishlistGrid.innerHTML = '';
        wishlistEmpty.hidden = false;
        clearBtn.hidden = true;
      });
    }

    fetchAllProducts().then(function (products) {
      var byHandle = {};
      products.forEach(function (p) {
        byHandle[p.handle] = p;
      });

      var found = false;
      wishlistHandles.forEach(function (handle) {
        var product = byHandle[handle];
        if (!product) return;
        found = true;
        var card = buildMiniCard(template, product, {
          onRemove: function () {
            remove(handle);
            if (!wishlistGrid.children.length) wishlistEmpty.hidden = false;
          },
        });
        wishlistGrid.appendChild(card);
      });

      if (!found && wishlistEmpty) wishlistEmpty.hidden = false;
      renderViewed(products);
    });
  }

  function renderViewed(preloadedProducts) {
    var viewedGrid = document.querySelector('[data-viewed-grid]');
    var viewedEmpty = document.querySelector('[data-viewed-empty]');
    var template = document.getElementById('acct-mini-card-template');
    var viewedIds = getViewedIds();

    if (!viewedGrid || !template) return;

    if (viewedIds.length === 0) {
      if (viewedEmpty) viewedEmpty.hidden = false;
      return;
    }

    var productsPromise = preloadedProducts ? Promise.resolve(preloadedProducts) : fetchAllProducts();

    productsPromise.then(function (products) {
      var byId = {};
      products.forEach(function (p) {
        byId[String(p.id)] = p;
      });

      var found = false;
      viewedIds.forEach(function (id) {
        var product = byId[String(id)];
        if (!product) return;
        found = true;
        var card = buildMiniCard(template, product, { removable: false });
        viewedGrid.appendChild(card);
      });

      if (!found && viewedEmpty) viewedEmpty.hidden = false;
    });
  }

  Suhara.wishlist = {
    has: has,
    add: add,
    remove: remove,
    toggle: toggle,
    handleToggleClick: handleToggleClick,
  };

  window.SuharaWishlist = {
    renderDashboard: renderDashboard,
  };

  document.addEventListener('DOMContentLoaded', hydrateButtons);
})();
